import { useMemo } from 'react'
import { KanbanBoard, type KanbanItem, type KanbanStage } from '@/components/ui'
import { Chip } from '@/components/ui'
import { usePreferences } from '@/shell/stores/preferences'
import type { IntakeSummary, Stage } from '../api/types'
import {
  BOARD_STAGES,
  STAGE_SHORT_LABELS,
  STAGE_VARIANT,
  transitionAllowed,
} from '../utils/stage-meta'

// Sprint 22 hot-fix (2026-05-08) — moduleId for the kanbanStageOrder
// preference key. Stable string — don't rename without migrating
// existing user prefs.
const MODULE_ID = 'sales-inbox'

/**
 * Sprint 22 Chunk 5 — kanban view for the sales-inbox.
 *
 * Eight columns, one per commercial stage. Drag a card to a target
 * column to dispatch a transition; ALLOWED_TRANSITIONS gates which
 * column-pairs accept the drop (server-side enforcement is identical).
 *
 * Cards open the detail drawer on click (handled by parent via
 * onSelect). The kanban itself does not edit state directly; all
 * mutations route through the standard useTransitionIntake mutation.
 */

interface IntakeKanbanItem extends KanbanItem {
  intake: IntakeSummary
}

export interface SalesPipelineKanbanProps {
  items: IntakeSummary[]
  selectedId?: string
  onSelect: (intake: IntakeSummary) => void
  onTransition: (intake: IntakeSummary, target: Stage) => void
}

function kanbanAccent(
  v: 'neutral' | 'success' | 'warning' | 'danger' | 'info',
): 'neutral' | 'success' | 'warning' | 'danger' {
  // KanbanStage's accent doesn't have an `info` slot — fold info → neutral.
  if (v === 'info') return 'neutral'
  return v
}

const STAGE_BY_ID: Record<Stage, KanbanStage> = BOARD_STAGES.reduce(
  (acc, s) => {
    acc[s] = { id: s, label: STAGE_SHORT_LABELS[s], accent: kanbanAccent(STAGE_VARIANT[s]) }
    return acc
  },
  {} as Record<Stage, KanbanStage>,
)

/**
 * Compose the active column order from the user's preference + the
 * canonical STAGES list. User-listed stages sort first (in their order);
 * any canonical stage missing from the user pref falls back to the
 * tail in canonical order. This handles the case where new stages are
 * added later and existing users haven't seen them yet.
 */
function composeStageOrder(userOrder: ReadonlyArray<string>): KanbanStage[] {
  const seen = new Set<string>()
  const out: KanbanStage[] = []
  for (const id of userOrder) {
    if (id in STAGE_BY_ID && !seen.has(id)) {
      out.push(STAGE_BY_ID[id as Stage])
      seen.add(id)
    }
  }
  for (const s of BOARD_STAGES) {
    if (!seen.has(s)) {
      out.push(STAGE_BY_ID[s])
      seen.add(s)
    }
  }
  return out
}

export function SalesPipelineKanban({
  items,
  selectedId,
  onSelect,
  onTransition,
}: SalesPipelineKanbanProps) {
  const userOrder = usePreferences((s) => s.kanbanStageOrder[MODULE_ID]) ?? []
  const setKanbanStageOrder = usePreferences((s) => s.setKanbanStageOrder)

  const stages = useMemo(() => composeStageOrder(userOrder), [userOrder])

  const kanbanItems: IntakeKanbanItem[] = useMemo(
    () =>
      items.map((intake) => ({
        id: intake.id,
        stageId: intake.status,
        intake,
      })),
    [items],
  )

  return (
    <KanbanBoard<IntakeKanbanItem>
      stages={stages}
      items={kanbanItems}
      selectedId={selectedId}
      allowedTransitions={(from, to) =>
        transitionAllowed(from as Stage, to as Stage)
      }
      onAdvance={(item, _from, to) => onTransition(item.intake, to as Stage)}
      onSelect={(item) => onSelect(item.intake)}
      onStageReorder={(next) => setKanbanStageOrder(MODULE_ID, next)}
      renderCard={(item) => <IntakeKanbanCard intake={item.intake} />}
      scrollRestorationKey="sales-inbox"
    />
  )
}

function IntakeKanbanCard({ intake }: { intake: IntakeSummary }) {
  const total = intake.total_amount
    ? `$${Number(intake.total_amount).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })}`
    : null
  return (
    <div
      className={`flex flex-col gap-1.5 ${
        intake.is_overdue ? '-ml-3 border-l-2 border-l-danger pl-2.5' : ''
      }`}
      title={
        intake.is_overdue
          ? 'Past due being confirmed (>2 business days)'
          : undefined
      }
    >
      {intake.is_overdue ? (
        <span className="text-role-metadata font-medium text-danger">
          Overdue
        </span>
      ) : null}
      <div className="text-role-data-primary truncate" title={intake.customer_name ?? ''}>
        {intake.customer_name ?? <em className="text-tertiary">unknown customer</em>}
      </div>
      <div className="text-role-data-secondary truncate">
        PO {intake.customer_po_number ?? '—'}
      </div>
      {intake.order_date ? (
        <div className="text-role-metadata">Ordered {formatDate(intake.order_date)}</div>
      ) : null}
      <div className="flex items-center gap-2 text-xs">
        <span className="text-tertiary">{intake.line_count} line{intake.line_count === 1 ? '' : 's'}</span>
        {total ? <span className="text-tertiary">· {total}</span> : null}
      </div>
      {intake.notes ? (
        <div
          className="line-clamp-2 text-role-metadata italic"
          title={intake.notes}
        >
          {intake.notes}
        </div>
      ) : null}
      {intake.extraction_state !== 'complete' ? (
        <Chip
          size="xs"
          variant={intake.extraction_state === 'failed' ? 'danger' : 'neutral'}
          title={intake.extraction_state === 'failed' && intake.extraction_error
            ? intake.extraction_error
            : undefined}
        >
          {extractionLabel(intake.extraction_state)}
        </Chip>
      ) : null}
      {intake.acknowledged_at ? (
        <Chip
          size="xs"
          variant="info"
          title={`Acknowledged by ${intake.acknowledged_by ?? 'unknown'}`}
        >
          Acknowledged
        </Chip>
      ) : null}
      {intake.odoo_so_name && intake.odoo_so_url ? (
        <a
          href={intake.odoo_so_url}
          target="_blank"
          rel="noreferrer"
          className="text-role-metadata text-info hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {intake.odoo_so_name}
        </a>
      ) : intake.odoo_so_name ? (
        <div className="text-role-metadata">{intake.odoo_so_name}</div>
      ) : null}
    </div>
  )
}

function extractionLabel(state: 'pending' | 'running' | 'complete' | 'failed'): string {
  if (state === 'pending') return 'Extraction queued'
  if (state === 'running') return 'Extracting…'
  if (state === 'failed') return 'Extraction failed'
  return ''
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}
